import React, { useState, useEffect, useRef } from 'react';
import { 
  View, 
  Text, 
  TextInput, 
  ScrollView, 
  TouchableOpacity, 
  Modal, 
  StyleSheet, 
  Platform, 
  Animated,
  Dimensions 
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const ThankYouCardModal = ({ isVisible, onClose, onSend, senderName }) => {
  const [message, setMessage] = useState("");
  const [recipients, setRecipients] = useState("");
  const [sendMethod, setSendMethod] = useState('email'); // 'email' or 'sms' or 'both'
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const toastOpacity = useRef(new Animated.Value(0)).current;

  const { width: screenWidth } = Dimensions.get('window');
  const isMobile = screenWidth < 768;

  // Reset state when modal closes
  useEffect(() => {
    if (!isVisible) {
      setMessage("");
      setRecipients("");
      setSendMethod('email');
      setShowToast(false);
      setIsSending(false);
    }
  }, [isVisible]);

  // Toast notification functions
  const showToastNotification = (message) => {
    setToastMessage(message);
    setShowToast(true);
    
    Animated.sequence([
      Animated.timing(toastOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.delay(2500),
      Animated.timing(toastOpacity, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start(() => setShowToast(false));
  };

  // Parse recipients - support comma, space, or tab separated emails
  const parseRecipients = (input) => {
    if (!input || !input.trim()) return [];
    
    // Split by comma, semicolon, space, or tab
    const emails = input
      .split(/[,;\s\t]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0);
    
    return emails;
  };

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate phone number format (US format)
  const isValidPhone = (phone) => {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    // Check if it's 10 or 11 digits (with optional country code)
    return cleaned.length === 10 || (cleaned.length === 11 && cleaned[0] === '1');
  };

  // Determine if input looks like email or phone
  const identifyRecipientType = (input) => {
    if (input.includes('@')) return 'email';
    if (/[\d\-\(\)\s\+]/.test(input)) return 'phone';
    return 'unknown';
  };

  const handleSendCards = async () => {
    // Validation
    if (!message.trim()) {
      showToastNotification("❌ Please write a thank you message");
      return;
    }

    if (!recipients.trim()) {
      showToastNotification("❌ Please enter at least one recipient");
      return;
    }

    const recipientList = parseRecipients(recipients);
    
    if (recipientList.length === 0) {
      showToastNotification("❌ No valid recipients found");
      return;
    }

    // Separate emails and phone numbers based on send method
    let emailList = [];
    let smsList = [];

    if (sendMethod === 'email' || sendMethod === 'both') {
      // Filter for emails
      emailList = recipientList.filter(r => identifyRecipientType(r) === 'email');
      
      // Validate all emails
      const invalidEmails = emailList.filter(email => !isValidEmail(email));
      if (invalidEmails.length > 0 && sendMethod === 'email') {
        showToastNotification(`❌ Invalid email(s): ${invalidEmails.join(', ')}`);
        return;
      }
    }

    if (sendMethod === 'sms' || sendMethod === 'both') {
      // Filter for phone numbers
      smsList = recipientList.filter(r => identifyRecipientType(r) === 'phone');
      
      // Validate all phone numbers
      const invalidPhones = smsList.filter(phone => !isValidPhone(phone));
      if (invalidPhones.length > 0 && sendMethod === 'sms') {
        showToastNotification(`❌ Invalid phone(s): ${invalidPhones.join(', ')}`);
        return;
      }
    }

    // If 'both' mode, auto-detect and separate
    if (sendMethod === 'both') {
      emailList = recipientList.filter(r => isValidEmail(r));
      smsList = recipientList.filter(r => isValidPhone(r));
      
      if (emailList.length === 0 && smsList.length === 0) {
        showToastNotification("❌ No valid email addresses or phone numbers found");
        return;
      }
    }

    setIsSending(true);

    try {
      // Send the card data to parent component
      await onSend({
        message: message.trim(),
        emailRecipients: emailList,
        smsRecipients: smsList,
        sendMethod: sendMethod,
        senderName: senderName || 'Graduate',
        personalizedMessages: []
      });

      const totalRecipients = emailList.length + smsList.length;
      let successMessage = `✅ Sending ${totalRecipients} thank you card(s)`;
      if (emailList.length > 0) successMessage += ` (${emailList.length} email)`;
      if (smsList.length > 0) successMessage += ` (${smsList.length} SMS)`;
      
      showToastNotification(successMessage);
      
      // Close modal after a brief delay
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      
      showToastNotification("❌ Failed to send cards. Please try again.");
      setIsSending(false);
    }
  };

  return (
    <Modal
      visible={isVisible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={[
          styles.modalView,
          isMobile && { width: '95%', maxWidth: '95%' }
        ]}>
          {/* Header */}
          <View style={styles.headerContainer}>
            <Text style={styles.modalTitle}>Send Thank You Card</Text>
            <TouchableOpacity 
              onPress={onClose}
              style={styles.closeIconButton}
            >
              <Ionicons name="close" size={24} color="#2d3748" />
            </TouchableOpacity>
          </View>

          {/* Scrollable Content */}
          <ScrollView 
            style={styles.scrollableContent}
            contentContainerStyle={styles.scrollableContentContainer}
          >
            {/* Instruction Text */}
            <Text style={styles.instructionText}>
              Write your thank you message and add recipient email(s) or phone number(s)
            </Text>

            {/* Message Input */}
            <View style={styles.formContainer}>
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Your Thank You Message</Text>
                <TextInput
                  style={[styles.input, styles.messageInput]}
                  placeholder="Write your heartfelt message here..."
                  placeholderTextColor="#a0aec0"
                  multiline
                  value={message}
                  onChangeText={setMessage}
                  maxLength={500}
                />
                <Text style={styles.characterCount}>
                  {message.length}/500 characters
                </Text>
              </View>

              {/* Send Method Selection */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Send via</Text>
                <View style={styles.sendMethodContainer}>
                  <TouchableOpacity
                    style={[
                      styles.sendMethodButton,
                      sendMethod === 'email' && styles.sendMethodButtonActive
                    ]}
                    onPress={() => setSendMethod('email')}
                  >
                    <Ionicons 
                      name="mail" 
                      size={20} 
                      color={sendMethod === 'email' ? '#ffffff' : '#4c956c'} 
                    />
                    <Text style={[
                      styles.sendMethodText,
                      sendMethod === 'email' && styles.sendMethodTextActive
                    ]}>
                      Email
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[
                      styles.sendMethodButton,
                      sendMethod === 'sms' && styles.sendMethodButtonActive
                    ]}
                    onPress={() => setSendMethod('sms')}
                  >
                    <Ionicons 
                      name="chatbubble" 
                      size={20} 
                      color={sendMethod === 'sms' ? '#ffffff' : '#4c956c'} 
                    />
                    <Text style={[
                      styles.sendMethodText,
                      sendMethod === 'sms' && styles.sendMethodTextActive
                    ]}>
                      SMS
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[
                      styles.sendMethodButton,
                      sendMethod === 'both' && styles.sendMethodButtonActive
                    ]}
                    onPress={() => setSendMethod('both')}
                  >
                    <Ionicons 
                      name="layers" 
                      size={20} 
                      color={sendMethod === 'both' ? '#ffffff' : '#4c956c'} 
                    />
                    <Text style={[
                      styles.sendMethodText,
                      sendMethod === 'both' && styles.sendMethodTextActive
                    ]}>
                      Both
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>

              {/* Recipients Input */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>
                  {sendMethod === 'email' ? 'Recipient Email(s)' :
                   sendMethod === 'sms' ? 'Recipient Phone Number(s)' :
                   'Recipients (Email or Phone)'}
                  <Text style={{ fontSize: 12, color: '#6c757d', fontWeight: 'normal' }}>
                    {' '}(separate multiple with commas)
                  </Text>
                </Text>
                <TextInput
                  style={[styles.input, styles.recipientsInput]}
                  placeholder={
                    sendMethod === 'email' ? 'email@example.com, another@example.com' :
                    sendMethod === 'sms' ? '+1234567890, +9876543210' :
                    'email@example.com, +1234567890'
                  }
                  placeholderTextColor="#a0aec0"
                  multiline
                  value={recipients}
                  onChangeText={setRecipients}
                  keyboardType={sendMethod === 'sms' ? 'phone-pad' : 'default'}
                  autoCapitalize="none"
                />
                {recipients.trim() && (
                  <Text style={styles.characterCount}>
                    {parseRecipients(recipients).length} recipient(s)
                  </Text>
                )}
              </View>
            </View>

            {/* Send Button */}
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[
                  styles.primaryButton,
                  (!message.trim() || !recipients.trim() || isSending) && styles.disabledButton
                ]}
                onPress={handleSendCards}
                disabled={!message.trim() || !recipients.trim() || isSending}
              >
                <Ionicons 
                  name={isSending ? "hourglass-outline" : "send"} 
                  size={20} 
                  color="#ffffff" 
                  style={styles.buttonIcon} 
                />
                <Text style={styles.primaryButtonText}>
                  {isSending ? 'Sending...' : 'Send Thank You Cards'}
                </Text>
              </TouchableOpacity>
            </View>
          </ScrollView>

          {/* Toast Notification */}
          {showToast && (
            <Animated.View style={[styles.toast, { opacity: toastOpacity }]}>
              <Ionicons name="information-circle" size={24} color="#ffffff" style={styles.toastIcon} />
              <Text style={styles.toastText}>{toastMessage}</Text>
            </Animated.View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalView: {
    width: '95%',
    maxWidth: 800,
    maxHeight: '90%',
    backgroundColor: '#fefde2',
    borderRadius: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 20,
    elevation: 10,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e6d690',
    backgroundColor: '#fefde2',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#2d3748',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'Roboto',
  },
  closeIconButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#f0f0f0',
  },
  scrollableContent: {
    flex: 1,
  },
  scrollableContentContainer: {
    padding: 24,
    paddingTop: 16,
    paddingBottom: 60,
  },
  instructionText: {
    fontSize: 16,
    color: '#4c956c',
    textAlign: 'center',
    fontWeight: '600',
    padding: 12,
    backgroundColor: '#f0f9f4',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#4c956c',
    marginBottom: 20,
  },
  formContainer: {
    width: '100%',
    marginBottom: 24,
  },
  inputContainer: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2d3748',
    marginBottom: 8,
  },
  input: {
    width: '100%',
    padding: 16,
    borderWidth: 2,
    borderColor: '#d4c5a9',
    borderRadius: 12,
    fontSize: 16,
    color: '#2d3748',
    backgroundColor: '#ffffff',
  },
  messageInput: {
    height: 120,
    textAlignVertical: 'top',
  },
  recipientsInput: {
    height: 80,
    textAlignVertical: 'top',
  },
  characterCount: {
    fontSize: 12,
    color: '#718096',
    textAlign: 'right',
    marginTop: 4,
  },
  sendMethodContainer: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
  },
  sendMethodButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#4c956c',
    backgroundColor: '#ffffff',
    gap: 8,
  },
  sendMethodButtonActive: {
    backgroundColor: '#4c956c',
    borderColor: '#4c956c',
  },
  sendMethodText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4c956c',
  },
  sendMethodTextActive: {
    color: '#ffffff',
  },
  buttonContainer: {
    width: '100%',
    alignItems: 'center',
  },
  primaryButton: {
    flexDirection: 'row',
    backgroundColor: '#4c956c',
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 200,
    shadowColor: '#4c956c',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  disabledButton: {
    backgroundColor: '#a0aec0',
    shadowColor: '#a0aec0',
  },
  buttonIcon: {
    marginRight: 8,
  },
  primaryButtonText: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: '700',
  },
  toast: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    right: 20,
    backgroundColor: '#4c956c',
    padding: 16,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  toastIcon: {
    marginRight: 8,
  },
  toastText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
  },
});

export default ThankYouCardModal;
